// SPDX-License-Identifier: AGPL-3.0-or-later

import type { PrefillItem } from '../api/types.js';

export interface StalePromptCardProps {
  linkId: string;
  label: string;
  item: PrefillItem;
  /** Wall-clock so the relative-time copy is consistent across cards in a render pass. */
  now: number;
  onConfirm: () => void;
  onEdit: () => void;
}

/**
 * Friendly per-item "Last updated N months ago — is this still correct?"
 * card. Replaces the terse `stale-banner` with copy that names the field
 * and offers two clear next steps so the patient never wonders what the
 * card is asking them to do.
 */
export function StalePromptCard({
  linkId,
  label,
  item,
  now,
  onConfirm,
  onEdit,
}: StalePromptCardProps) {
  const answered = item.answeredAt;
  const ago = answered === null || answered === undefined ? null : humanRelativeAge(answered, now);

  // Surface a previewable value so the patient knows what they previously
  // said. We keep this short — long answers get truncated downstream by
  // CSS, and structured values (Coding, attachment) collapse to their
  // type so we don't dump a JSON blob into the page.
  const preview = previewValue(item.value);

  return (
    <div className="stale-card" data-link-id={linkId}>
      <div className="stale-card-body">
        <p className="stale-card-question">
          <strong>{label}</strong>{' '}
          {ago !== null ? (
            <span className="stale-card-age muted">— last updated {ago}.</span>
          ) : (
            <span className="stale-card-age muted">— no recent update on file.</span>
          )}
        </p>
        {preview !== null ? (
          <p className="stale-card-preview muted">
            On file: <span>{preview}</span>
          </p>
        ) : null}
        <p className="stale-card-question-prompt">Is this still correct?</p>
      </div>
      <div className="stale-card-actions">
        <button type="button" className="stale-card-edit" onClick={onEdit}>
          Update it
        </button>
        <button type="button" className="stale-card-confirm" onClick={onConfirm}>
          Yes, still correct
        </button>
      </div>
    </div>
  );
}

const MONTH_MS = 30 * 24 * 60 * 60 * 1000;
const YEAR_MS = 365 * 24 * 60 * 60 * 1000;

function humanRelativeAge(answeredAtIso: string, now: number): string {
  const then = Date.parse(answeredAtIso);
  if (!Number.isFinite(then)) return 'a while ago';
  const delta = Math.max(0, now - then);
  if (delta < 7 * 24 * 60 * 60 * 1000) {
    const days = Math.max(1, Math.round(delta / (24 * 60 * 60 * 1000)));
    return days === 1 ? '1 day ago' : `${days} days ago`;
  }
  if (delta < MONTH_MS) {
    const weeks = Math.max(1, Math.round(delta / (7 * 24 * 60 * 60 * 1000)));
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
  }
  if (delta < YEAR_MS) {
    const months = Math.max(1, Math.round(delta / MONTH_MS));
    return months === 1 ? '1 month ago' : `${months} months ago`;
  }
  const years = Math.max(1, Math.round(delta / YEAR_MS));
  return years === 1 ? '1 year ago' : `${years} years ago`;
}

function previewValue(value: unknown): string | null {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'string') return value.length > 60 ? `${value.slice(0, 57)}…` : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return `${value.length} item${value.length === 1 ? '' : 's'}`;
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (typeof obj['display'] === 'string') return obj['display'];
    if (typeof obj['code'] === 'string') return obj['code'];
    if (typeof obj['title'] === 'string') return `file: ${obj['title']}`;
    return '(structured value)';
  }
  return null;
}
